import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { MapPin, Save, Eye, EyeOff, Map } from 'lucide-react';
import MapPicker from '../../components/MapPicker';
import PlaceAutocompleteInput from '../../components/PlaceAutocompleteInput';

export default function BusinessLocationSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const { data: locationData } = await api.get('/company/location');
      setData(locationData);
      if (locationData.business_lat && locationData.business_lng) {
        setShowMap(true);
      }
    } catch (e) {
      toast.error('Failed to load location settings');
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put('/company/location', data);
      toast.success('Location settings saved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleMapChange = (lat, lng) => {
    setData({ ...data, business_lat: lat, business_lng: lng });
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <MapPin className="w-6 h-6" />
            Business Location
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Set your business location for accurate distance calculations and public display
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6 space-y-6">
        {/* Address autocomplete: selecting a suggestion fills lat/lng so
            distance/location warnings can work. The free-text field below
            still lets the user override or refine the street name. */}
        <div>
          <PlaceAutocompleteInput
            label="Search for your business address"
            placeholder="Start typing (e.g. Carol-an Ayungon, Dumaguete Boulevard)"
            value={data.business_lat && data.business_lng ? {
              label: data.business_address || data.business_map_label,
              address: data.business_address,
              lat: data.business_lat,
              lng: data.business_lng,
            } : null}
            onChange={(place) => {
              if (!place) {
                setData({ ...data, business_lat: null, business_lng: null });
                setShowMap(false);
                return;
              }
              setData({
                ...data,
                business_address: place.address || data.business_address || place.label,
                business_lat: place.lat,
                business_lng: place.lng,
                business_map_label: data.business_map_label || (place.label ? place.label.split(',')[0] : ''),
              });
              if (place.lat && place.lng) setShowMap(true);
            }}
            countryCode="ph"
            testId="bus-loc-place"
            helperText="Pick a suggestion to set GPS coordinates. You can still edit the address text below."
          />
        </div>

        {/* Address Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Business Address</label>
            <input
              type="text"
              value={data.business_address || ''}
              onChange={(e) => setData({ ...data, business_address: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Street address"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Barangay</label>
              <input
                type="text"
                value={data.business_barangay || ''}
                onChange={(e) => setData({ ...data, business_barangay: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Barangay"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City/Municipality</label>
              <input
                type="text"
                value={data.business_city || ''}
                onChange={(e) => setData({ ...data, business_city: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="City"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Province</label>
              <input
                type="text"
                value={data.business_province || ''}
                onChange={(e) => setData({ ...data, business_province: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Province"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Country</label>
              <input
                type="text"
                value={data.business_country || 'Philippines'}
                onChange={(e) => setData({ ...data, business_country: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Map Picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">Location on Map</label>
            <button
              onClick={() => setShowMap(!showMap)}
              className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
            >
              <Map className="w-4 h-4" />
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          </div>
          {showMap && (
            <MapPicker
              lat={data.business_lat}
              lng={data.business_lng}
              onLocationChange={handleMapChange}
            />
          )}
          <div className="mt-2 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Latitude</label>
              <input
                type="number"
                step="0.000001"
                value={data.business_lat || ''}
                onChange={(e) => setData({ ...data, business_lat: parseFloat(e.target.value) || null })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="e.g., 9.3068"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Longitude</label>
              <input
                type="number"
                step="0.000001"
                value={data.business_lng || ''}
                onChange={(e) => setData({ ...data, business_lng: parseFloat(e.target.value) || null })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="e.g., 123.3054"
              />
            </div>
          </div>
        </div>

        {/* Additional Fields */}
        <div>
          <label className="block text-sm font-medium mb-1">Map Label / Location Name</label>
          <input
            type="text"
            value={data.business_map_label || ''}
            onChange={(e) => setData({ ...data, business_map_label: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g., Main Office, Downtown Branch"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Service Area Description</label>
          <textarea
            value={data.service_area_text || ''}
            onChange={(e) => setData({ ...data, service_area_text: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            rows={2}
            placeholder="e.g., We serve Dumaguete City and nearby towns"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Pickup Location Notes</label>
          <textarea
            value={data.pickup_location_notes || ''}
            onChange={(e) => setData({ ...data, pickup_location_notes: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            rows={3}
            placeholder="Special instructions for customers about pickup location"
          />
        </div>

        {/* Public Visibility Mode */}
        <div>
          <label className="block text-sm font-medium mb-2">Public Location Display</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="location_mode"
                checked={data.public_location_mode === 'exact'}
                onChange={() => setData({ ...data, public_location_mode: 'exact' })}
              />
              <div className="flex-1">
                <div className="font-medium text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Exact Address
                </div>
                <div className="text-xs text-gray-600">Show full address and exact map location publicly</div>
              </div>
            </label>
            <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="location_mode"
                checked={data.public_location_mode === 'approximate'}
                onChange={() => setData({ ...data, public_location_mode: 'approximate' })}
              />
              <div className="flex-1">
                <div className="font-medium text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Approximate Area (Recommended)
                </div>
                <div className="text-xs text-gray-600">Show city/area only, hide exact address</div>
              </div>
            </label>
            <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="location_mode"
                checked={data.public_location_mode === 'hidden'}
                onChange={() => setData({ ...data, public_location_mode: 'hidden' })}
              />
              <div className="flex-1">
                <div className="font-medium text-sm flex items-center gap-2">
                  <EyeOff className="w-4 h-4" />
                  Hidden
                </div>
                <div className="text-xs text-gray-600">Don't show location publicly</div>
              </div>
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={save}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Location'}
          </button>
        </div>
      </div>
    </div>
  );
}
